// Wave 20.17 — Global liability release gate.
//
// Wraps every authenticated route. When the logged-in user hasn't
// accepted the current Liability Release, renders a blocking modal
// with the doc body and an "I agree" button. No route in the
// authenticated surface can proceed until they accept.
//
// Public routes (login, register, magic-link, /shared/, /peak/,
// account setup) are skipped so the guard doesn't block sign-in or
// the identity-setup flow.
//
// Fail-closed: if /legal/current returns non-OK, we still block —
// a broken legal endpoint shouldn't let sessions through. This is
// the opposite of IdentityGuard's fail-open behavior; identity is
// a nice-to-have, liability is a legal requirement.

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { colors } from '../theme';

const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';
const API = process.env.REACT_APP_API || 'https://api.aero.chat';

// Paths that skip the gate. Any authenticated in-app route (session
// start, dashboards, account) is NOT on this list — those are the
// surfaces we're protecting.
const SKIP_PREFIXES = [
  '/',           // Homepage (marketing). Kept public.
  '/login',
  '/register',
  '/password',
  '/verify',
  '/about',
  '/privacy',
  '/terms',
  '/liability',
  '/rapha/register',
  '/rapha/welcome',
  '/shared/',
  '/peak/',
  '/dropped/',
  '/u/',
  '/login/magic',
];

interface LegalDoc {
  kind: 'liability';
  version: string;
  effectiveAt: string;
  body: string;
  mine: { accepted: boolean; lastAcceptedVersion: string | null };
}

interface Props {
  children: React.ReactNode;
}

export default function LiabilityGuard({ children }: Props) {
  const { pathname } = useLocation();
  const [doc, setDoc] = useState<LegalDoc | null>(null);
  const [status, setStatus] = useState<'idle' | 'loaded' | 'error' | 'unauth'>('idle');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setStatus('unauth');
      return;
    }
    try {
      const res = await fetch(`${API}/legal/current?kind=liability`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        setStatus('unauth');
        return;
      }
      if (!res.ok) {
        setStatus('error');
        return;
      }
      const body = (await res.json()) as LegalDoc;
      setDoc(body);
      setStatus('loaded');
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load();
    // Re-check on route change so a fresh login (adds a JWT) triggers
    // the fetch, and a route switch after acceptance flips through.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const onPublic = SKIP_PREFIXES.some((p) =>
    p === '/' ? pathname === '/' : pathname.startsWith(p),
  );

  const accept = async () => {
    if (!doc) return;
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/legal/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ kind: 'liability', version: doc.version }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `HTTP ${res.status}`);
      }
      // Re-fetch so mine.accepted flips to true and the modal closes.
      await load();
    } catch (e: any) {
      setError(e?.message || 'Accept failed');
    } finally {
      setSaving(false);
    }
  };

  // Skip conditions — never block: public routes, unauth (no token),
  // or before the first fetch completes. `error` state deliberately
  // still shows the modal so a broken endpoint doesn't unlock the app.
  if (onPublic || status === 'unauth' || status === 'idle') return <>{children}</>;

  const alreadyAccepted = status === 'loaded' && !!doc && doc.mine.accepted;
  if (alreadyAccepted) return <>{children}</>;

  // Wave 20.24 — Do NOT render children while blocking. Rendering the
  // page behind the modal (a) lets the inner IdentityGuard's own
  // modal mount and stack on top of this one, and (b) leaks the
  // underlying UI through the backdrop.
  return (
    <>
      <Dialog
        open
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              backgroundColor: colors.surfaceElevated,
              border: `1px solid ${colors.border}`,
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Liability Release &amp; Waiver
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mb: 2 }}>
            You must accept this release before using aero.chat sessions.
            {doc?.mine.lastAcceptedVersion && doc.mine.lastAcceptedVersion !== doc.version && (
              <> This release has been updated since you last accepted; please review and re-accept.</>
            )}
          </Typography>
          {status === 'error' && !doc && (
            <Typography sx={{ color: colors.danger, fontSize: '0.9rem', mb: 2 }}>
              Couldn't load the release document. Retry in a moment.
            </Typography>
          )}
          <Box
            sx={{
              maxHeight: 360,
              overflowY: 'auto',
              p: 2,
              borderRadius: 2,
              backgroundColor: colors.bg,
              border: `1px solid ${colors.border}`,
              whiteSpace: 'pre-wrap',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.82rem',
              lineHeight: 1.6,
              color: colors.text,
            }}
          >
            {doc?.body ?? 'Loading…'}
          </Box>
          {error && (
            <Typography sx={{ color: colors.danger, mt: 2, fontSize: '0.85rem' }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {status === 'error' && (
            <Button onClick={load} disabled={saving} sx={{ color: colors.textMuted }}>
              Retry
            </Button>
          )}
          <Button
            variant="contained"
            onClick={accept}
            disabled={saving || !doc}
            sx={{
              backgroundColor: colors.brand,
              color: colors.textInverse,
              fontWeight: 800,
              '&:hover': { backgroundColor: colors.brandBright },
            }}
          >
            {saving ? <CircularProgress size={18} /> : 'I agree'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
